import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'theme';

// Applies a `data-theme` attribute on <html> that theme-aware SCSS reads via
// CSS custom properties (see _theme.scss). Kept as a small root service
// (rather than component state) so every page shares one source of truth
// and the choice survives navigation without needing a store.
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private current: ThemeMode;
  // CSS vars update automatically on the data-theme attribute change, but
  // canvas/SVG-rendered content (ApexCharts) is baked in at render time and
  // needs to be told to re-render — components that draw charts subscribe
  // to this to rebuild them when the user toggles theme live.
  private themeSubject: BehaviorSubject<ThemeMode>;
  readonly theme$: Observable<ThemeMode>;

  constructor() {
    this.current = this.resolveInitialTheme();
    this.apply(this.current);
    this.themeSubject = new BehaviorSubject<ThemeMode>(this.current);
    this.theme$ = this.themeSubject.asObservable();
  }

  get theme(): ThemeMode {
    return this.current;
  }

  get isDark(): boolean {
    return this.current === 'dark';
  }

  toggle(): void {
    this.setTheme(this.current === 'dark' ? 'light' : 'dark');
  }

  setTheme(theme: ThemeMode): void {
    this.current = theme;
    localStorage.setItem(STORAGE_KEY, theme);
    this.apply(theme);
    this.themeSubject.next(theme);
  }

  private apply(theme: ThemeMode): void {
    document.documentElement.setAttribute('data-theme', theme);
  }

  private resolveInitialTheme(): ThemeMode {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  }
}
